import { Button, Grid2, Menu, MenuItem, Skeleton } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { Icons } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { ItemImage } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { encodeSkuId } from '@/core/sku'

export * from './fragments'

import { Action, State } from '../viewModel'

type SelectButtonCellProps = {
  onFilter: Action['onFilter']
}

type ItemImageListCellProps = {
  loading: boolean
  userId: string
  hasNextPage: boolean
  portfolios: State['portfolios']
  onFetchMore: Action['onFetchMore']
}

export const UserOwnedItemsTemplate: Template<State, Action> = ({
  state,
  onFilter,
  onFetchMore,
}) => {
  const { t } = useTranslation('userOwnedItems')
  const { routerNames, routerPages, generatePath } = useRouter()
  return (
    <>
      <Head title={[routerNames.UserOwnedItems, state.userName]} />
      <BaseLayout
        header={{
          back: true,
          defaultBackPage: generatePath(routerPages.UserProfile, {
            userId: state.userId ?? '#',
          }),

          title: t('title'),
        }}
      >
        <SelectButtonCell onFilter={onFilter} />
        <ItemImageListCell
          userId={state.userId}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          portfolios={state.portfolios}
          onFetchMore={onFetchMore}
        />
      </BaseLayout>
    </>
  )
}

const filters = ['filterProcedure']

const SelectButtonCell: Cell<SelectButtonCellProps> = ({ onFilter }) => {
  const { t } = useTranslation('userOwnedItems')
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleMenuItemClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, index: number) => {
      setSelectedIndex(index)
      setAnchorEl(null)
      onFilter(index)
    },
    [onFilter]
  )
  return (
    <CellFrame display="flex" justifyContent="flex-end" sx={{ pb: 2, px: 5 }}>
      <Button
        variant="text"
        onClick={handleClick}
        sx={{ color: 'text.primary', mr: -2 }}
      >
        {t(filters[selectedIndex])}
        <Icons.DownArrow sx={{ pl: 2 }} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {filters.map((filter, index) => (
          <MenuItem
            key={index}
            sx={{ bgcolor: 'grey.50' }}
            selected={selectedIndex === index}
            onClick={event => handleMenuItemClick(event, index)}
          >
            {t(filter)}
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}

const ItemImageListCell: Cell<ItemImageListCellProps> = ({
  loading,
  hasNextPage,
  portfolios,
  onFetchMore,
  userId,
}) => {
  const { t } = useTranslation('userOwnedItems')
  const { routerPages, generatePath, router } = useRouter()

  return (
    <CellFrame sx={{ px: 5, py: 2 }}>
      <ScrollFetcher
        nodes={portfolios?.edges.map(item => (
          <ItemImage
            key={item.node.item.id}
            onClick={() =>
              router.push({
                pathname: generatePath(routerPages.UserItemDetail, {
                  userId: userId,
                  itemId: item.node.item.id,
                }),
                search: item.node.item.sku
                  ? `?skuId=${encodeSkuId(item.node.item.sku?.id ?? '')}`
                  : '',
              })
            }
            item={item.node.item as never}
            ownedCount={item.node.number}
            showIcon
          />
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid2}
        componentProps={{
          container: true,
          spacing: 3,
        }}
        emptyMessage={t('userOwnedItemEmptyMessage')}
        loadingComponent={
          <Grid2 container spacing={3}>
            {Array.from(Array(9).keys()).map(key => (
              <Grid2 size={{ xs: 4, md: 3 }} key={key}>
                <Skeleton
                  variant="rectangular"
                  sx={{ width: '100%', height: 166 }}
                />
              </Grid2>
            ))}
          </Grid2>
        }
        itemComponent={Grid2}
        itemComponentProps={{
          size: { xs: 4, md: 3 },
          sx: { textAlign: 'center' },
        }}
      />
    </CellFrame>
  )
}
